import type { CreditCurrency } from '@rehub/pricing';

export interface CreateCreditCheckoutParams {
  /** Stable pack id (`credits_25`) or subscription id (`starter`). Omit for custom. */
  packId?: string;
  planId?: string;
  currency: CreditCurrency;
  mode: 'payment' | 'subscription';
  credits: number;
  /** Stripe price id when known; server prefers packId resolution. */
  priceId?: string;
  quantity?: number;
  userId: string;
  successUrl: string;
  cancelUrl: string;
  purchaseType?: 'credit_pack' | 'credit_subscription' | 'custom_credits';
  extraMetadata?: Record<string, string>;
}

type SupabaseLike = {
  functions: {
    invoke: (
      name: string,
      options: { body: Record<string, unknown>; headers?: Record<string, string> }
    ) => Promise<{ data: { url?: string; error?: string } | null; error: { message: string } | null }>;
  };
};

/** Shared invoke helper used by app wrappers around `@rehub/buy-credits`. */
export async function createCreditCheckoutViaSupabase(
  supabase: SupabaseLike,
  params: CreateCreditCheckoutParams
): Promise<string> {
  const {
    packId,
    planId,
    currency,
    mode,
    credits,
    priceId,
    quantity = 1,
    userId,
    successUrl,
    cancelUrl,
    purchaseType,
    extraMetadata,
  } = params;

  const body: Record<string, unknown> = {
    mode,
    quantity,
    client_reference_id: userId,
    userId,
    successUrl,
    cancelUrl,
    currency,
    metadata: {
      credit_amount: String(credits),
      credits: String(credits),
      user_id: userId,
      currency,
      purchase_type: purchaseType || (mode === 'subscription' ? 'credit_subscription' : 'credit_pack'),
      ...(packId ? { pack_id: packId } : {}),
      ...(planId ? { plan_id: planId } : {}),
      ...(extraMetadata || {}),
    },
  };

  if (packId) body.packId = packId;
  if (planId) body.planId = planId;
  if (priceId) body.priceId = priceId;

  const invokePromise = supabase.functions.invoke('api-payment', {
    body,
    headers: { 'Content-Type': 'application/json' },
  });

  const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
    setTimeout(() => {
      resolve({
        data: null,
        error: {
          message:
            'Request timed out. Your network, browser extension, or firewall may be blocking checkout.',
        },
      });
    }, 15000);
  });

  const { data, error } = (await Promise.race([invokePromise, timeoutPromise])) as Awaited<
    typeof invokePromise
  >;

  if (error) {
    const msg =
      error.message?.includes('Failed to send') || error.message?.includes('fetch failed')
        ? 'Connection blocked by ad-blocker. Please disable it for this site.'
        : error.message || 'Payment service error.';
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  if (data?.url) return data.url;
  throw new Error('No payment URL received.');
}
